import { Typography } from '@mui/material';

import useGetLedgerAccounts from 'src/features/finance/hooks/useGetLedgerAccounts';

import DisplayCurrency from '../DisplayCurrency';
import { Flex } from '../Flex';

type LineItem = {
    ledgerAccount?: string;
    productService?: string;
    description?: string | React.ReactNode;
    quantity: number;
    netAmount?: number;
    totalLineVat?: number;
    lineTotal?: number;
};

interface LineItemTableProps {
    columnHeaders: string[];
    data: LineItem[];
    showLedgerAccount?: boolean;
    showProductService?: boolean;
    usersCurrency?: string;
}

const LineItemTable = ({
    columnHeaders,
    data,
    showLedgerAccount = false,
    usersCurrency,
}: LineItemTableProps) => {
    const { ledgerAccounts } = useGetLedgerAccounts();
    const visibleColumnsCount = columnHeaders.length;
    const width = `${100 / visibleColumnsCount}%`;

    const getLedgerAccountName = (id?: string) => {
        if (!id) return '';
        return (
            ledgerAccounts.find(({ value }) => {
                return value === id;
            })?.label || ''
        );
    };

    return (
        <Flex border="1px solid #ffea99" flexDirection="column" overflow="scroll">
            <Flex flex={1} padding="16px 8px" bgcolor="#FFFAE5" justifyContent="space-between">
                {columnHeaders.map((header) => {
                    return (
                        <Flex
                            width={width}
                            key={header}
                            color="#1D1D1B"
                            bgcolor="#FFFAE5"
                            justifyContent={
                                ['Quantity', 'Net Amount', 'Total Line Vat', 'Line Total'].includes(
                                    header
                                )
                                    ? 'flex-end'
                                    : 'flex-start'
                            }
                        >
                            {header}
                        </Flex>
                    );
                })}
            </Flex>

            <div data-testid="table-data">
                {data?.map((item, index) => {
                    return (
                        <Flex
                            bgcolor={index % 2 !== 0 ? '#FFFAE5' : 'white'}
                            key={index}
                            padding="16px 8px"
                            justifyContent="space-between"
                            flex={1}
                        >
                            {columnHeaders.includes('Ledger Account') && showLedgerAccount && (
                                <Flex width={width} justifyContent="flex-start">
                                    <Typography
                                        fontSize="14px"
                                        fontWeight="400"
                                        color="#1d1d1b"
                                        lineHeight="20px"
                                    >
                                        {getLedgerAccountName(item.ledgerAccount)}
                                    </Typography>
                                </Flex>
                            )}

                            {columnHeaders.includes('Description') && (
                                <Flex width={width} justifyContent="flex-start">
                                    <Typography
                                        fontSize="14px"
                                        fontWeight="400"
                                        color="#1d1d1b"
                                        lineHeight="20px"
                                    >
                                        {item.description}
                                    </Typography>
                                </Flex>
                            )}

                            {columnHeaders.includes('Quantity') && (
                                <Flex width={width} justifyContent="flex-end">
                                    {item.quantity}
                                </Flex>
                            )}

                            {columnHeaders.includes('Net Amount') && (
                                <Flex width={width} justifyContent="flex-end">
                                    <DisplayCurrency
                                        centsAmount={item.netAmount}
                                        usersCurrency={usersCurrency}
                                    />
                                </Flex>
                            )}
                            {columnHeaders.includes('Total Line Vat') && (
                                <Flex width={width} justifyContent="flex-end">
                                    <DisplayCurrency
                                        centsAmount={item.totalLineVat}
                                        usersCurrency={usersCurrency}
                                    />
                                </Flex>
                            )}
                            {columnHeaders.includes('Line Total') && (
                                <Flex width={width} justifyContent="flex-end">
                                    <DisplayCurrency
                                        centsAmount={item.lineTotal}
                                        usersCurrency={usersCurrency}
                                    />
                                </Flex>
                            )}
                        </Flex>
                    );
                })}
            </div>
        </Flex>
    );
};

export default LineItemTable;
